import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { LanguageService } from '../../../../core/services/language.service';
import { CardOptionListComponent } from '../../../../osp-ui/components/form/card-option-list/card-option-list.component';
import { AlarmTypeInfo } from '../../contract/alarm-type-info.contract';
import { AlarmType } from '../../enums/alarm-type.enum';
import { AlarmTypeIconPipe } from '../../pipes/alarm-type-icon.pipe';
import { AlarmTypeResolverPipe } from '../../pipes/alarm-type-resolver.pipe';
import { AlarmTypeGroupControl } from './enums/alarm-type-group-control.enum';

interface AlarmTypeOption {
  type: AlarmType;
  icon: string;
  label: string;
  config?: AlarmTypeInfo;
}

interface AlarmTypeGroup {
  title: string;
  alarmTypes: AlarmTypeOption[];
}

interface FormGroupConfig {
  [key: string]: AlarmType | null;
}

@Component({
  selector: 'osp-alarm-type-selection',
  standalone: true,
  imports: [CardOptionListComponent, ReactiveFormsModule],
  templateUrl: './alarm-type-selection.component.html',
  styleUrls: ['./alarm-type-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlarmTypeSelectionComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() options!: Map<AlarmType, AlarmTypeInfo>;
  @Input() alarmTypeControl: AbstractControl | null | undefined;

  @Output() onAlarmTypeChange: EventEmitter<AlarmType> = new EventEmitter();

  protected contractAndServiceOptions: AlarmTypeOption[] = [];
  protected operationalStatusOptions: AlarmTypeOption[] = [];
  protected costsAndConditionOptions: AlarmTypeOption[] = [];
  protected selectedOption = signal<string | null>(null);
  protected form!: FormGroup;
  protected controlNames = AlarmTypeGroupControl;

  constructor(
    private languageService: LanguageService,
    private formBuilder: FormBuilder,
    private alarmTypeTitleResolver: AlarmTypeResolverPipe,
    private alarmTypeIconResolver: AlarmTypeIconPipe,
  ) {}

  protected readonly contractAndService: AlarmTypeGroup = {
    title: this.translate('modules.notification.alarmRuleAddEdit.labels.contractAndService'),
    alarmTypes: [
      {
        type: AlarmType.OPERATING_HOURS,
        icon: this.alarmTypeIconResolver.transform(AlarmType.OPERATING_HOURS),
        label: this.alarmTypeTitleResolver.transform(AlarmType.OPERATING_HOURS),
      },
      {
        type: AlarmType.OPERATING_HOURS_CONTRACT,
        icon: this.alarmTypeIconResolver.transform(AlarmType.OPERATING_HOURS_CONTRACT),
        label: this.alarmTypeTitleResolver.transform(AlarmType.OPERATING_HOURS_CONTRACT),
      },
      {
        type: AlarmType.GUARANTEE,
        icon: this.alarmTypeIconResolver.transform(AlarmType.GUARANTEE),
        label: this.alarmTypeTitleResolver.transform(AlarmType.GUARANTEE),
      },
      {
        type: AlarmType.FULL_SERVICE_CONTRACT_END,
        icon: this.alarmTypeIconResolver.transform(AlarmType.FULL_SERVICE_CONTRACT_END),
        label: this.alarmTypeTitleResolver.transform(AlarmType.FULL_SERVICE_CONTRACT_END),
      },
      {
        type: AlarmType.LEASING_SERVICE_CONTRACT_END,
        icon: this.alarmTypeIconResolver.transform(AlarmType.LEASING_SERVICE_CONTRACT_END),
        label: this.alarmTypeTitleResolver.transform(AlarmType.LEASING_SERVICE_CONTRACT_END),
      },
    ],
  };

  protected readonly operationalStatus: AlarmTypeGroup = {
    title: this.translate('modules.notification.alarmRuleAddEdit.labels.operationalStatus'),
    alarmTypes: [
      {
        type: AlarmType.TANK_FILL_LEVEL,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TANK_FILL_LEVEL),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TANK_FILL_LEVEL),
      },
      {
        type: AlarmType.GLOBAL_GEOFENCE_VIOLATION,
        icon: this.alarmTypeIconResolver.transform(AlarmType.GLOBAL_GEOFENCE_VIOLATION),
        label: this.alarmTypeTitleResolver.transform(AlarmType.GLOBAL_GEOFENCE_VIOLATION),
      },
      {
        type: AlarmType.TIMEFENCING,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TIMEFENCING),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TIMEFENCING),
      },
      {
        type: AlarmType.TRANSPORT_TASK_CREATION,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TRANSPORT_TASK_CREATION),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TRANSPORT_TASK_CREATION),
      },
      {
        type: AlarmType.TRANSPORT_TASK_RETURNED,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TRANSPORT_TASK_RETURNED),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TRANSPORT_TASK_RETURNED),
      },
      {
        type: AlarmType.NEW_DAMAGE_ADDED,
        icon: this.alarmTypeIconResolver.transform(AlarmType.NEW_DAMAGE_ADDED),
        label: this.alarmTypeTitleResolver.transform(AlarmType.NEW_DAMAGE_ADDED),
      },
      {
        type: AlarmType.INACTIVE_TELEMATICS_UNIT,
        icon: this.alarmTypeIconResolver.transform(AlarmType.INACTIVE_TELEMATICS_UNIT),
        label: this.alarmTypeTitleResolver.transform(AlarmType.INACTIVE_TELEMATICS_UNIT),
      },
      {
        type: AlarmType.THEFT_PROTECTION,
        icon: this.alarmTypeIconResolver.transform(AlarmType.THEFT_PROTECTION),
        label: this.alarmTypeTitleResolver.transform(AlarmType.THEFT_PROTECTION),
      },
    ],
  };

  protected readonly costsAndCondition: AlarmTypeGroup = {
    title: this.translate('modules.notification.alarmRuleAddEdit.labels.costsAndCondition'),
    alarmTypes: [
      {
        type: AlarmType.TOTAL_COSTS,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TOTAL_COSTS),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TOTAL_COSTS),
      },
      {
        type: AlarmType.DAMAGE_COSTS,
        icon: this.alarmTypeIconResolver.transform(AlarmType.DAMAGE_COSTS),
        label: this.alarmTypeTitleResolver.transform(AlarmType.DAMAGE_COSTS),
      },
      {
        type: AlarmType.REPAIR_COSTS,
        icon: this.alarmTypeIconResolver.transform(AlarmType.REPAIR_COSTS),
        label: this.alarmTypeTitleResolver.transform(AlarmType.REPAIR_COSTS),
      },
      {
        type: AlarmType.BATTERY_VOLTAGE,
        icon: this.alarmTypeIconResolver.transform(AlarmType.BATTERY_VOLTAGE),
        label: this.alarmTypeTitleResolver.transform(AlarmType.BATTERY_VOLTAGE),
      },
      {
        type: AlarmType.DEF_TANK_LEVEL,
        icon: this.alarmTypeIconResolver.transform(AlarmType.DEF_TANK_LEVEL),
        label: this.alarmTypeTitleResolver.transform(AlarmType.DEF_TANK_LEVEL),
      },
      {
        type: AlarmType.TELEMATICS_UNIT_BATTERY_STATUS,
        icon: this.alarmTypeIconResolver.transform(AlarmType.TELEMATICS_UNIT_BATTERY_STATUS),
        label: this.alarmTypeTitleResolver.transform(AlarmType.TELEMATICS_UNIT_BATTERY_STATUS),
      },
    ],
  };

  public ngOnInit(): void {
    this.distributeOptions();
    this.buildForm();
    this.subscribeToChanges();
  }

  public get contractAndServiceControl(): AbstractControl | null | undefined {
    return this.form.get(this.controlNames.CONTRACT_AND_SERVICE);
  }

  public get costsAndConditionControl(): AbstractControl | null | undefined {
    return this.form.get(this.controlNames.COSTS_AND_CONDITION);
  }

  public get operationalStatusControl(): AbstractControl | null | undefined {
    return this.form.get(this.controlNames.OPERATIONAL_STATUS);
  }

  private mergeConfig(alarmTypeGroup: AlarmTypeOption[], targetArray: AlarmTypeOption[]): void {
    alarmTypeGroup.forEach((alarmType) => {
      const config = this.options.get(alarmType.type);
      if (config) {
        targetArray.push({ ...alarmType, config });
      }
    });
  }

  private distributeOptions(): void {
    this.contractAndServiceOptions = [];
    this.operationalStatusOptions = [];
    this.costsAndConditionOptions = [];

    this.mergeConfig(this.contractAndService.alarmTypes, this.contractAndServiceOptions);
    this.mergeConfig(this.operationalStatus.alarmTypes, this.operationalStatusOptions);
    this.mergeConfig(this.costsAndCondition.alarmTypes, this.costsAndConditionOptions);
  }

  private buildForm(): void {
    const selectedAlarmType = this.alarmTypeControl?.value;

    const controlSelections: {
      [key: string]: AlarmTypeOption[];
    } = {
      [this.controlNames.CONTRACT_AND_SERVICE]: this.contractAndService.alarmTypes,
      [this.controlNames.COSTS_AND_CONDITION]: this.costsAndCondition.alarmTypes,
      [this.controlNames.OPERATIONAL_STATUS]: this.operationalStatus.alarmTypes,
    };

    const formGroupConfig = this.createFormConfig(controlSelections, selectedAlarmType);
    this.form = this.formBuilder.group(formGroupConfig);
  }

  private createFormConfig(
    controlSelections: { [key: string]: AlarmTypeOption[] },
    selectedAlarmType: AlarmType,
  ): FormGroupConfig {
    const formGroupConfig = Object.keys(controlSelections).reduce((config, controlName) => {
      const alarmTypes = controlSelections[controlName];
      const isOptionSelected = alarmTypes.some((alarmType) => alarmType.type === selectedAlarmType);

      config[controlName] = isOptionSelected ? selectedAlarmType : null;

      return config;
    }, {} as FormGroupConfig);

    return formGroupConfig;
  }

  private subscribeToChanges(): void {
    [
      this.controlNames.CONTRACT_AND_SERVICE,
      this.controlNames.COSTS_AND_CONDITION,
      this.controlNames.OPERATIONAL_STATUS,
    ].forEach((controlName) => {
      this.form
        .get(controlName)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((newValue: AlarmType) => {
          this.setOtherControlsToNull(controlName);

          this.alarmTypeControl?.patchValue(newValue);
          this.onAlarmTypeChange.emit(newValue);
        });
    });
  }

  private setOtherControlsToNull(changedControlName: string): void {
    [
      this.controlNames.CONTRACT_AND_SERVICE,
      this.controlNames.COSTS_AND_CONDITION,
      this.controlNames.OPERATIONAL_STATUS,
    ].forEach((controlName) => {
      if (controlName !== changedControlName) {
        this.form.get(controlName)?.setValue(null, { emitEvent: false });
      }
    });
  }

  private translate(key: string, interpolateParams?: object): string {
    return this.languageService.getInstant(key, interpolateParams);
  }
}
